// Single-view stitched MP4 player.
//
// Double-buffered: two <video> elements alternate playing segment N while the
// other preloads N+1. On `ended` we swap activeVid + advance segIndex, so
// consecutive AVA clips play back-to-back as one continuous clip (LLD 13 §8).
//
// The hook owns the imperative `<video>` plumbing and exposes a small,
// rerender-friendly view of clip state plus a few imperative methods. It is
// deliberately NOT pure-React — `<video>` is an effectful element and trying
// to drive its `.currentTime` / `.src` / `.play()` through declarative props
// fights the browser. Instead we mount the `<video>`s in a child component
// (`PlayerStage`) via `attach()` and the hook drives them directly.
//
// 416 MEDIA_NOT_FOUND from the pre-signed AVA GET inside a segment is a
// recording GAP, not an error — we catch the `<video>.error` event, mark
// the segment errored, and `advance()` to the next segment. (LLD 13 §13)

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "@/api";
import { clipsClient, type ClipTier } from "@/api/clipsClient";

import {
  segAtVirtual,
  synthesizeSegments,
  totalDurationMs,
  virtualAtSeg,
  withInitialState,
  type PlayableSegment,
  type SegmentState,
} from "../helpers/segments";

/** AVA HD (1080p) 504s past ~10 min at the gateway transcode (LLD 13 §10). */
export const HD_MAX_MS = 10 * 60_000;

export interface ClipMeta {
  /** Doorbot id of the source camera. */
  doorbot_id: string;
  /** Display name (used by OSD + clip-info). */
  name: string;
  /** Display location (used by clip-info subline). */
  locId?: string;
}

export interface ClipState {
  meta: ClipMeta;
  /** Wall-clock start of the stitched clip (ms). */
  startMs: number;
  /** Total stitched duration (ms). */
  durMs: number;
  segments: PlayableSegment[];
  /** Current segment index in `segments`. */
  segIndex: number;
  /** Virtual offset into the stitched clip (ms). */
  virtualMs: number;
  playing: boolean;
  /** The tier the segments were resolved at. */
  tier: ClipTier;
  /** Whether HD is currently capped by the 10-min wall. */
  hdDisabled: boolean;
}

export interface LoadOptions {
  /** When true, skip refocusing the timeline window on the new clip. */
  keepWindow?: boolean;
  /** Preserve a virtual seek + play state across a camera switch. */
  seekMs?: number;
  keepPlaying?: boolean;
}

export interface StitchPlayerApi {
  clip: ClipState | null;
  /** Whether the load() promise is in flight (drives the spinner overlay). */
  loading: boolean;
  /** Most recent gap surface (a 416 from AVA = camera not recording then). */
  lastGap: { startMs: number; jumpedToMs: number | null } | null;

  /**
   * Imperatively bind two `<video>` elements (mounted in PlayerStage). The
   * stitch engine drives them directly. Pass `null`s on unmount.
   */
  attach(videoA: HTMLVideoElement | null, videoB: HTMLVideoElement | null): void;
  /** Which of the two `<video>`s is currently visible (0 or 1). */
  activeVidIndex: number;

  load(meta: ClipMeta, startMs: number, durMs: number, opts?: LoadOptions): Promise<void>;
  play(): void;
  pause(): void;
  toggle(): void;
  seekVirtual(ms: number, keepPlaying?: boolean): void;
  setSpeed(speed: number): void;
  speed: number;
  setTier(tier: ClipTier): void;
  tier: ClipTier;
  /** ±10 min — re-resolves the range extended on the chosen side. */
  extend(direction: -1 | 1): Promise<void>;
  /** Drop the loaded clip + reset to "no clip" state. */
  clear(): void;
  /** Acknowledge the lastGap toast (callers clear it after surfacing). */
  clearGap(): void;
}

interface AttachedVids {
  a: HTMLVideoElement | null;
  b: HTMLVideoElement | null;
}

const NO_VIDS: AttachedVids = { a: null, b: null };

/** Mark all already-played segments as ready in-place. */
function markPriorReady(segs: PlayableSegment[], upToIndex: number): void {
  for (let k = 0; k < upToIndex && k < segs.length; k++) {
    if (segs[k]!.state !== "ready") segs[k]!.state = "ready";
  }
}

export function useStitchPlayer(): StitchPlayerApi {
  const [clip, setClipState] = useState<ClipState | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeVidIndex, setActiveVidIndex] = useState(0);
  const [speed, setSpeedState] = useState(1);
  const [tier, setTierState] = useState<ClipTier>("scan");
  const [lastGap, setLastGap] = useState<StitchPlayerApi["lastGap"]>(null);

  // Refs let synchronous `<video>` event handlers see the latest state
  // without going through a re-render. The state above is for the UI.
  const clipRef = useRef<ClipState | null>(null);
  const activeRef = useRef(0);
  const speedRef = useRef(1);
  const tierRef = useRef<ClipTier>("scan");
  const vidsRef = useRef<AttachedVids>(NO_VIDS);
  const loadTokenRef = useRef(0);

  // Keep refs in lockstep with state so the imperative path always sees fresh values.
  useEffect(() => { clipRef.current = clip; }, [clip]);
  useEffect(() => { activeRef.current = activeVidIndex; }, [activeVidIndex]);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => { tierRef.current = tier; }, [tier]);

  /**
   * Re-publish a mutated clip-state object. We mutate `segments[i].state`
   * imperatively (cheaper than re-allocating the array on every load tick),
   * but still need React to re-render the timeline + render-bar — so we
   * shallow-clone the wrapper.
   */
  const publishClip = useCallback((next: ClipState | null) => {
    clipRef.current = next;
    setClipState(next ? { ...next, segments: [...next.segments] } : null);
  }, []);

  const attach = useCallback(
    (videoA: HTMLVideoElement | null, videoB: HTMLVideoElement | null) => {
      vidsRef.current = { a: videoA, b: videoB };
    },
    [],
  );

  const showVid = useCallback((idx: 0 | 1 | -1) => {
    const { a, b } = vidsRef.current;
    if (a) a.classList.toggle("show", idx === 0);
    if (b) b.classList.toggle("show", idx === 1);
  }, []);

  const updateVirtualFromTime = useCallback(() => {
    const c = clipRef.current;
    if (!c) return;
    const v = activeRef.current === 0 ? vidsRef.current.a : vidsRef.current.b;
    let elapsed = 0;
    for (let k = 0; k < c.segIndex; k++) elapsed += c.segments[k]!.duration_ms;
    if (v && v.getAttribute("src") && !Number.isNaN(v.currentTime)) {
      elapsed += v.currentTime * 1000;
    }
    const next: ClipState = { ...c, virtualMs: Math.min(elapsed, c.durMs) };
    clipRef.current = next;
    setClipState(next);
  }, []);

  const playSegment = useCallback(
    async (i: number): Promise<void> => {
      const c = clipRef.current;
      if (!c || i >= c.segments.length) {
        if (c) {
          const ended: ClipState = { ...c, playing: false };
          publishClip(ended);
        }
        return;
      }
      const seg = c.segments[i]!;
      seg.state = "loading";
      // Re-publish to repaint the render-bar.
      publishClip({ ...c, segIndex: i });

      const url = clipsClient.resolveMediaUrl(seg.playback_url);
      const active = activeRef.current === 0 ? vidsRef.current.a : vidsRef.current.b;
      const idle = activeRef.current === 0 ? vidsRef.current.b : vidsRef.current.a;
      if (!active) return;

      if (url) {
        if (active.getAttribute("src") !== url) active.src = url;
        const cTier = clipRef.current!.tier;
        active.playbackRate = cTier === "hd" ? Math.min(speedRef.current, 2) : speedRef.current;
        active.muted = speedRef.current > 2 || cTier === "scan";
        try {
          await active.play();
        } catch {
          // play() rejects on autoplay-block; we keep state, the user can hit play.
        }
        seg.state = "playing";
        markPriorReady(c.segments, i);
        showVid(activeRef.current as 0 | 1);

        const next = c.segments[i + 1];
        if (next && next.playback_url && idle) {
          const nurl = clipsClient.resolveMediaUrl(next.playback_url);
          if (nurl) {
            next.state = "loading";
            idle.src = nurl;
            idle.load();
            idle.oncanplaythrough = () => {
              if (next.state === "loading") {
                next.state = "ready";
                publishClip(clipRef.current);
              }
            };
          }
        }

        active.onended = () => {
          seg.state = "ready";
          if (clipRef.current && clipRef.current.segIndex + 1 < clipRef.current.segments.length) {
            const newActive = activeRef.current === 0 ? 1 : 0;
            activeRef.current = newActive;
            setActiveVidIndex(newActive);
            void playSegment(clipRef.current.segIndex + 1);
          } else {
            const c2 = clipRef.current;
            if (c2) publishClip({ ...c2, playing: false });
          }
        };
        active.ontimeupdate = updateVirtualFromTime;
        active.onerror = () => {
          // 416 / decode error / cors block — treat the segment as a gap.
          seg.state = "error";
          const cur = clipRef.current;
          if (!cur) return;
          if (cur.segIndex + 1 < cur.segments.length) {
            const jumpedTo = cur.segments[cur.segIndex + 1]!.start_ms;
            setLastGap({ startMs: seg.start_ms, jumpedToMs: jumpedTo });
            const newActive = activeRef.current === 0 ? 1 : 0;
            activeRef.current = newActive;
            setActiveVidIndex(newActive);
            void playSegment(cur.segIndex + 1);
          } else {
            setLastGap({ startMs: seg.start_ms, jumpedToMs: null });
            publishClip({ ...cur, playing: false });
          }
        };

        const playing: ClipState = { ...clipRef.current!, playing: true };
        publishClip(playing);
      } else {
        // No url — `playback_url=null` is a synthetic-fallback gap. Don't play.
        seg.state = "queued";
        publishClip({ ...c });
      }
    },
    [publishClip, showVid, updateVirtualFromTime],
  );

  const load = useCallback(
    async (meta: ClipMeta, startMs: number, durMs: number, opts: LoadOptions = {}) => {
      const myToken = ++loadTokenRef.current;
      setLoading(true);
      // HD auto-cap (LLD 13 §10).
      const hdDisabled = durMs > HD_MAX_MS;
      let useTier: ClipTier = tierRef.current;
      if (useTier === "hd" && hdDisabled) {
        useTier = "scan";
        setTierState("scan");
        tierRef.current = "scan";
      }

      let segments: PlayableSegment[];
      try {
        const r = await clipsClient.resolve({
          device_id: meta.doorbot_id,
          start_ms: startMs,
          duration_ms: durMs,
          tier: useTier,
        });
        if (myToken !== loadTokenRef.current) return; // a newer load superseded us
        segments = withInitialState(r.segments);
        if (segments.length === 0) {
          // Empty resolve == no footage in this window. Synthesize an empty
          // shell so the UI shows the gap rather than blanking.
          segments = synthesizeSegments(startMs, durMs, useTier);
        }
      } catch (err) {
        if (myToken !== loadTokenRef.current) return;
        // Synthetic fallback — the synthesized cells have `playback_url=null`,
        // which the engine renders as gap placeholders rather than firing
        // doomed GETs.
        segments = synthesizeSegments(startMs, durMs, useTier);
        // Surface the resolve failure for callers that want a toast — but
        // keep loading happy so the timeline still renders.
        if (err instanceof ApiError) {
          // 403 = pre-consent timestamps; 400 range_too_wide; etc. Don't crash.
        }
      }

      const next: ClipState = {
        meta,
        startMs,
        durMs: totalDurationMs(segments) || durMs,
        segments,
        segIndex: 0,
        virtualMs: 0,
        playing: false,
        tier: useTier,
        hdDisabled,
      };
      activeRef.current = 0;
      setActiveVidIndex(0);
      publishClip(next);
      setLoading(false);
      setLastGap(null);

      if (opts.seekMs !== undefined) {
        // Seamless camera switch: jump straight to the preserved virtual time
        // and (optionally) play state.
        seekVirtualImpl(opts.seekMs, opts.keepPlaying ?? false);
      } else {
        await playSegment(0);
      }
      // Mark the suppressed unused-var (opts.keepWindow is consumed by the
      // container that calls focusWindow before invoking load).
      void opts.keepWindow;
    },
    // playSegment / publishClip / seekVirtualImpl are stable via closures
    // through refs; we list them anyway for hooks-rules clarity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [publishClip, playSegment],
  );

  const seekVirtualImpl = useCallback(
    (ms: number, keepPlaying: boolean) => {
      const c = clipRef.current;
      if (!c) return;
      const clamped = Math.max(0, Math.min(c.durMs, ms));
      const { i, offset } = segAtVirtual(c.segments, clamped);
      const seg = c.segments[i]!;
      const url = clipsClient.resolveMediaUrl(seg.playback_url);
      const active = activeRef.current === 0 ? vidsRef.current.a : vidsRef.current.b;
      if (active && url) {
        if (active.getAttribute("src") !== url) active.src = url;
        const apply = (): void => {
          try {
            active.currentTime = Math.min(offset / 1000, active.duration || offset / 1000);
          } catch {
            // setting currentTime can throw on networks errors; ignore.
          }
        };
        if (active.readyState >= 1) apply();
        else active.addEventListener("loadedmetadata", apply, { once: true });
        showVid(activeRef.current as 0 | 1);

        const next = c.segments[i + 1];
        const idle = activeRef.current === 0 ? vidsRef.current.b : vidsRef.current.a;
        if (next && idle) {
          const nurl = clipsClient.resolveMediaUrl(next.playback_url);
          if (nurl) {
            idle.src = nurl;
            idle.load();
          }
        }
        active.onended = () => {
          seg.state = "ready";
          if (clipRef.current && clipRef.current.segIndex + 1 < clipRef.current.segments.length) {
            const newActive = activeRef.current === 0 ? 1 : 0;
            activeRef.current = newActive;
            setActiveVidIndex(newActive);
            void playSegment(clipRef.current.segIndex + 1);
          }
        };
        active.ontimeupdate = updateVirtualFromTime;
        if (keepPlaying) active.play().catch(() => undefined);
      }
      const next: ClipState = {
        ...c,
        segIndex: i,
        virtualMs: clamped,
        playing: keepPlaying,
      };
      publishClip(next);
    },
    [playSegment, publishClip, showVid, updateVirtualFromTime],
  );

  const seekVirtual = useCallback(
    (ms: number, keepPlaying = false) => seekVirtualImpl(ms, keepPlaying),
    [seekVirtualImpl],
  );

  const play = useCallback(() => {
    const c = clipRef.current;
    if (!c) return;
    const seg = c.segments[c.segIndex];
    if (!seg) return;
    const url = clipsClient.resolveMediaUrl(seg.playback_url);
    const active = activeRef.current === 0 ? vidsRef.current.a : vidsRef.current.b;
    if (url && active) active.play().catch(() => undefined);
    publishClip({ ...c, playing: true });
  }, [publishClip]);

  const pause = useCallback(() => {
    const c = clipRef.current;
    if (!c) return;
    const { a, b } = vidsRef.current;
    if (a) a.pause();
    if (b) b.pause();
    publishClip({ ...c, playing: false });
  }, [publishClip]);

  const toggle = useCallback(() => {
    const c = clipRef.current;
    if (!c) return;
    if (c.playing) pause();
    else play();
  }, [pause, play]);

  const setSpeed = useCallback((s: number) => {
    speedRef.current = s;
    setSpeedState(s);
    const c = clipRef.current;
    const active = activeRef.current === 0 ? vidsRef.current.a : vidsRef.current.b;
    if (active && c) {
      active.playbackRate = c.tier === "hd" ? Math.min(s, 2) : s;
      active.muted = s > 2 || c.tier === "scan";
    }
  }, []);

  const setTier = useCallback(
    (next: ClipTier) => {
      if (next === tierRef.current) return;
      tierRef.current = next;
      setTierState(next);
      const c = clipRef.current;
      if (!c) return;
      // Tier change requires a resolve at the new tier; preserve the
      // current virtual position so the user doesn't lose their place.
      const at = c.virtualMs;
      void load(c.meta, c.startMs, c.durMs, { keepWindow: true, seekMs: at, keepPlaying: c.playing });
    },
    [load],
  );

  const extend = useCallback(
    async (direction: -1 | 1) => {
      const c = clipRef.current;
      if (!c) return;
      let newStart = c.startMs;
      let newDur = c.durMs;
      if (direction < 0) {
        newStart -= 10 * 60_000;
        newDur += 10 * 60_000;
      } else {
        newDur += 10 * 60_000;
      }
      // Preserve the current virtual time relative to the new start.
      const cursorWall = c.startMs + c.virtualMs;
      const newVirtual = Math.max(0, cursorWall - newStart);
      await load(c.meta, newStart, newDur, {
        seekMs: newVirtual,
        keepPlaying: c.playing,
      });
    },
    [load],
  );

  const clear = useCallback(() => {
    publishClip(null);
    setLastGap(null);
    const { a, b } = vidsRef.current;
    if (a) {
      a.pause();
      a.removeAttribute("src");
      a.load();
    }
    if (b) {
      b.pause();
      b.removeAttribute("src");
      b.load();
    }
  }, [publishClip]);

  const clearGap = useCallback(() => setLastGap(null), []);

  // Cleanup on unmount: tear down both <video>s.
  useEffect(() => {
    return () => {
      const { a, b } = vidsRef.current;
      if (a) {
        a.pause();
        a.removeAttribute("src");
      }
      if (b) {
        b.pause();
        b.removeAttribute("src");
      }
    };
  }, []);

  // Use virtualAtSeg as a sanity import so the engine and tests share the helper.
  void virtualAtSeg;
  // Suppress unused-import warning on SegmentState (re-exported by callers).
  void (null as unknown as SegmentState);

  return {
    clip,
    loading,
    lastGap,
    attach,
    activeVidIndex,
    load,
    play,
    pause,
    toggle,
    seekVirtual,
    setSpeed,
    speed,
    setTier,
    tier,
    extend,
    clear,
    clearGap,
  };
}
